import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Form, Input, Select, Checkbox, Tooltip, Spin, message } from 'antd';
import s from './s.module.less';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { editBasicInfo } from 'api/newUniprofile';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import FormValidationHelper from 'utils/validation';
import TitleTip from 'assets/channel/titleTip.jpg';
import CredentialTip from 'assets/channel/credentialTip.jpg';
import UsPhoneInput from 'components/form/USPhoneInput';
import GenderSelect from 'components/form/GenderSelect';
import { IBasicInfoStep } from 'types/newUniprofile';
import { isValidNpi } from 'utils/form';

interface BasicInfoStepProps {
    initValue?: IBasicInfoStep;
    refetch?: () => void;
    scheduleToken?: string;
}

const BasicInfoStep = forwardRef(({ initValue, refetch, scheduleToken }: BasicInfoStepProps, ref) => {
    const [form] = Form.useForm();
    const [npiRequired, setNpiRequired] = useState(true);
    const [sameAsPersonalPhone, setSameAsPersonalPhone] = useState(false);
    const [sameAsLoginEmail, setSameAsLoginEmail] = useState(false);

    useEffect(() => {
        if (initValue) {
            form.setFieldsValue(initValue);
            setSameAsPersonalPhone(!!initValue.practicePhone && initValue.tel === initValue.practicePhone);
            setSameAsLoginEmail(initValue.email === initValue.practiceEmail);
            setNpiRequired(!initValue.npiNotRequired);
        }
    }, [initValue]);

    // Lock the practice phone only when the saved value is a valid 10-digit number (same rule as
    // validateUSPhoneNumber); invalid/legacy junk data must stay editable or the user can never fix it.
    const practicePhoneLocked = (initValue?.practicePhone || '').replace(/\D/g, '').length === 10;

    const onSubmit = async () => {
        try {
            const values = await form.validateFields();
            const res = await editBasicInfo(values, scheduleToken);
            if (res.error) {
                message.error(res.error);
            } else {
                refetch?.();
            }
        } catch (error) {
            console.error('Form validation failed:', error);
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
        onValidate: () => {
            return form.validateFields();
        },
    }));

    const renderEditForm = () => {
        return (
            <Form form={form} layout="vertical" className={s.form}>
                <div className={s.formRow}>
                    <Form.Item
                        label={
                            <div className={s.formLabel}>
                                Title
                                <Tooltip
                                    color="#FFFFFF"
                                    overlayInnerStyle={{ padding: '20px', width: '400px' }}
                                    title={
                                        <div className={s.tipText}>
                                            Title will appear before the name. <img src={TitleTip} />
                                        </div>
                                    }
                                >
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </div>
                        }
                        name="title"
                        rules={[{ max: 10, message: 'Exceeded maximum character length of 10' }]}
                        className="flex-1"
                    >
                        <Input type="text" placeholder="Ex. Dr., Sr., Hon., Mr., Mrs., Ms." />
                    </Form.Item>
                </div>

                <div className={s.formRow}>
                    <div className="flex-1">
                        <Form.Item
                            name="npi"
                            label="NPI number"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (npiRequired && !value) {
                                            return Promise.reject('NPI number is a required field');
                                        }
                                        if (npiRequired && !isValidNpi(value)) {
                                            return Promise.reject('Please enter a valid NPI number');
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input placeholder="Enter your NPI number" disabled={!npiRequired} maxLength={10} />
                        </Form.Item>
                        <Form.Item name="npiNotRequired" valuePropName="checked" style={{ marginTop: '-16px' }}>
                            <Checkbox
                                onChange={(e) => {
                                    setNpiRequired(!e.target.checked);
                                    form.setFields([
                                        {
                                            name: 'npi',
                                            errors: [],
                                        },
                                    ]);
                                }}
                            >
                                My practice doesn&apos;t require an NPI
                            </Checkbox>
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="billingNpi"
                        label={
                            <div className={s.formLabel}>
                                Billing NPI
                                <Tooltip
                                    color="#FFFFFF"
                                    overlayInnerStyle={{ padding: '12px', width: '260px' }}
                                    title={
                                        <div className={s.tipText2}>
                                            Your 10-digit organizational NPI used for billing. This is not your
                                            individual NPI.
                                        </div>
                                    }
                                >
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </div>
                        }
                        rules={[
                            {
                                pattern: /^\d{10}$/,
                                message: 'Please enter a valid 10-digit Billing NPI',
                            },
                        ]}
                        className="flex-1"
                    >
                        <Input placeholder="Enter your billing NPI number" maxLength={10} />
                    </Form.Item>
                </div>

                <div className={s.formRow}>
                    <div className="flex-1">
                        <Form.Item
                            name="practicePhone"
                            label={
                                <span className={s.formLabel}>
                                    Practice phone number
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        color="#FFFFFF"
                                        title={
                                            <div className={s.tipText2}>
                                                This can be the same as your personal phone number.
                                            </div>
                                        }
                                    >
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Practice phone number is a required field' },
                                FormValidationHelper.validateUSPhoneNumber('Please enter a 10-digit phone number'),
                            ]}
                            className={s.formItemHalf}
                        >
                            <UsPhoneInput disabled={sameAsPersonalPhone || practicePhoneLocked} />
                        </Form.Item>
                        <div style={{ marginTop: '-12px' }}>
                            <Checkbox
                                checked={sameAsPersonalPhone}
                                disabled={practicePhoneLocked}
                                onChange={(e) => {
                                    form.setFieldsValue({
                                        practicePhone: e.target.checked ? form.getFieldValue('tel') : '',
                                    });
                                    setSameAsPersonalPhone(e.target.checked);
                                }}
                            >
                                Same as personal phone number
                            </Checkbox>
                        </div>
                    </div>

                    <div className="flex-1">
                        <Form.Item
                            name="practiceEmail"
                            label={
                                <span className={s.formLabel}>
                                    Practice email address
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        color="#FFFFFF"
                                        title={
                                            <div className={s.tipText2}>This can be the same as your work email.</div>
                                        }
                                    >
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Practice email address is a required field' },
                                { type: 'email', message: 'Please enter a valid email address' },
                            ]}
                            className={s.formItemHalf}
                        >
                            <Input type="text" disabled={sameAsLoginEmail} />
                        </Form.Item>
                        <div style={{ marginTop: '-12px' }}>
                            <Checkbox
                                checked={sameAsLoginEmail}
                                onChange={(e) => {
                                    setSameAsLoginEmail(e.target.checked);
                                    form.setFieldsValue({
                                        practiceEmail: e.target.checked ? form.getFieldValue('email') : '',
                                    });
                                }}
                            >
                                Same as login email address
                            </Checkbox>
                        </div>
                    </div>
                </div>
            </Form>
        );
    };

    return (
        <div className={s.wrap}>
            <div className={s.title}>Basic information</div>
            {renderEditForm()}
        </div>
    );
});

export default BasicInfoStep;
